import { useEffect, useRef } from 'react';
import styled from 'styled-components';
import { useFormContext, Controller } from 'react-hook-form';
import { PlacesAutocomplete } from '../address-autocomplete';
import { media } from '@payright/web-components';
import {
  ControlledInputField,
  ControlledDropdown,
  ControlledDropdownWithInput,
} from '../form-fields';
import type {
  AddressDetailsData as BnplCheckoutApiAddressDetailsData,
  ShippingDetailsData as BnplCheckoutApiShippingDetailsData,
} from '@payright/types-bnpl-checkout-api';
import getConstants from '../../util/constants';
import { INCOME_FREQUENCIES } from '../../util/constants/defaults';
import { FeatureFlags, useFeatures } from '../../providers/features-provider';

const {
  REGION_STATES,
  RESIDENTIAL_STATUS,
  TIME_IN_ADDRESS,
  LABEL_STATE,
  LABEL_SUBURB,
  LESS_THAN_6_MONTHS,
} = getConstants();

export type AddressDetailsData = BnplCheckoutApiAddressDetailsData;
export type ShippingDetailsData = BnplCheckoutApiShippingDetailsData;

export type CustomerAddressDetailsProps = {
  existingCustomer: boolean;
  editing: boolean;
};

const SCCustomerAddressDetails = styled.div`
  .address-body {
    display: grid;
    grid-template-columns: auto;
    gap: 1.5rem;
    align-items: flex-end;

    .label {
      display: inline-flex;
      flex-wrap: wrap;
      column-gap: 0.5rem;
      row-gap: 0.5rem;
      align-items: baseline;

      .label-secondary {
        width: 100%;
        color: ${(props) => props.theme.colours.plum.light};
        font-weight: lighter;
      }
    }

    .underline {
      text-decoration: underline;
    }
  }

  ${media.min.medium} {
    .address-body {
      grid-template-columns: repeat(2, minmax(10px, 1fr));
    }

    .md\\:col-span-2 {
      grid-column: span 2;
    }

    .md\\:col-start-1 {
      grid-column-start: 1;
    }
  }
`;

export const AddressDetails = ({ existingCustomer, editing }: CustomerAddressDetailsProps) => {
  const { errors, clearErrors, setValue, watch, getValues, unregister } = useFormContext<{
    addressDetails: AddressDetailsData;
    shippingDetails: ShippingDetailsData;
  }>();

  const timeAtCurrentAddress = watch(
    'addressDetails.residentialTimeAtAddress'
  ) as AddressDetailsData['residentialTimeAtAddress'];

  const residentialStatus = watch(
    'addressDetails.residentialStatus'
  ) as AddressDetailsData['residentialStatus'];

  const handleStreetAutoComplete = (addressDetails: any) => {
    clearErrors('addressDetails');
    setValue('addressDetails.suburb', addressDetails.suburb);
    setValue('addressDetails.street', addressDetails.street);
    setValue('addressDetails.state', addressDetails.states);
    setValue('addressDetails.postcode', addressDetails.postcode);
  };

  const sectionRef = useRef<HTMLDivElement>(null);

  const {
    flagsConfig: {
      [FeatureFlags.RENT_VERIFICATION]: rentVerificationFeatureFlag,
      [FeatureFlags.MORTGAGE_AMOUNTS]: mortgageAmountsFeatureFlag,
    },
  } = useFeatures();

  useEffect(() => {
    if (mortgageAmountsFeatureFlag && residentialStatus !== 'Own with Mortgage') {
      setValue('addressDetails.apportionedMortgageAmount', 0);
      setValue('addressDetails.mortgageFrequency', 'weekly');
      unregister('addressDetails.apportionedMortgageAmount');
      unregister('addressDetails.mortgageFrequency');
    }
  }, [residentialStatus]);

  // Scroll to block (by ID) when user submits and validation errors
  useEffect(() => {
    if (Object.keys(errors).length > 0 && errors.addressDetails && sectionRef.current) {
      // check for null and focus target on block with errors
      sectionRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
  }, [errors]); // Errors object changes on submit

  return (
    <SCCustomerAddressDetails id="AddressSection">
      <div ref={sectionRef} style={{ position: 'relative', top: '-2em' }} />
      <div className="address-body">
        <Controller
          name={'addressDetails.street'}
          rules={{ required: 'Street address is required' }}
          render={({ onChange }) => {
            return (
              <PlacesAutocomplete
                className="md:col-span-2"
                name={'addressDetails.street'}
                handleInputChange={onChange}
                onStreetChange={handleStreetAutoComplete}
                readOnly={existingCustomer && !editing}
                error={errors.addressDetails?.street?.message}
                clearErrors={clearErrors}
              />
            );
          }}
        />
        <ControlledInputField
          name={'addressDetails.suburb'}
          rules={{ required: `${LABEL_SUBURB} is required` }}
          readOnly={existingCustomer && !editing}
          error={errors.addressDetails?.suburb?.message}
        >
          {LABEL_SUBURB}
        </ControlledInputField>
        <ControlledDropdown
          name={'addressDetails.state'}
          rules={{ required: `${LABEL_STATE} is required` }}
          options={REGION_STATES}
          readOnly={existingCustomer && !editing}
          error={errors.addressDetails?.state?.message}
        >
          {LABEL_STATE}
        </ControlledDropdown>
        <ControlledInputField
          name={'addressDetails.postcode'}
          rules={{ required: 'Postcode required' }}
          readOnly={existingCustomer && !editing}
          error={errors.addressDetails?.postcode?.message}
          type="number"
        >
          Postcode
        </ControlledInputField>
        <ControlledDropdown
          name={'addressDetails.residentialStatus'}
          rules={{ required: 'Residential status required' }}
          options={RESIDENTIAL_STATUS}
          readOnly={existingCustomer && !editing}
          error={errors.addressDetails?.residentialStatus?.message}
        >
          Residential Status
        </ControlledDropdown>
        <ControlledDropdown
          name={'addressDetails.residentialTimeAtAddress'}
          rules={{ required: 'Time at current address is required' }}
          error={errors.addressDetails?.residentialTimeAtAddress?.message}
          options={TIME_IN_ADDRESS}
          readOnly={existingCustomer && !editing}
        >
          Time at Current Address
        </ControlledDropdown>

        {timeAtCurrentAddress === LESS_THAN_6_MONTHS && (
          <>
            <ControlledDropdown
              name={'addressDetails.residentialTimeAtPreviousAddress'}
              rules={{
                validate: (value: string) => {
                  if (
                    getValues('addressDetails.residentialTimeAtAddress') === LESS_THAN_6_MONTHS &&
                    !value
                  ) {
                    return 'Time in previous address is required';
                  }
                },
              }}
              error={errors.addressDetails?.residentialTimeAtPreviousAddress?.message as string}
              options={TIME_IN_ADDRESS}
              readOnly={existingCustomer && !editing}
            >
              Time at Previous Address
            </ControlledDropdown>
          </>
        )}
        {rentVerificationFeatureFlag && residentialStatus === 'Renting' && (
          <ControlledDropdownWithInput
            dropdownName="addressDetails.rentFrequency"
            inputName="addressDetails.rentAmount"
            inputPrefix="$"
            options={INCOME_FREQUENCIES}
            inputRules={{
              required: 'Rent amount is required',
              validate: (value: any) => {
                if (isNaN(value)) {
                  return 'Rent amount must be a number';
                }
              },
            }}
            error={errors.addressDetails?.rentAmount?.message}
          >
            <span className="label">
              Rent
              <span className="label-secondary">
                If you share rent with someone, only include{' '}
                <span className="underline">your own portion</span>
              </span>
            </span>
          </ControlledDropdownWithInput>
        )}
        {mortgageAmountsFeatureFlag && residentialStatus === 'Own with Mortgage' && (
          <ControlledDropdownWithInput
            dropdownName="addressDetails.mortgageFrequency"
            inputName="addressDetails.apportionedMortgageAmount"
            inputPrefix="$"
            options={INCOME_FREQUENCIES}
            inputRules={{
              required: 'Mortgage amount is required',
              validate: (value: any) => {
                if (isNaN(value)) {
                  return 'Mortgage amount must be a number';
                }
              },
            }}
            error={errors.addressDetails?.apportionedMortgageAmount?.message}
          >
            <span className="label">
              Mortgage Repayment
              <span className="label-secondary">
                If your mortgage is shared with someone, only include your own portion
              </span>
            </span>
          </ControlledDropdownWithInput>
        )}
      </div>
    </SCCustomerAddressDetails>
  );
};
